import React, { useEffect, useState, useRef } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import {
  StyledItemDetails,
  StyledProductInformation,
  StyledDocumentsSection,
  Row,
  Column,
  StyledStorage,
  StyledNotificationsContainer
} from './ProductInformation.styles'
import { ClinTheme } from '../../../ClinTheme'
import { ClinSpacer } from '../../../components/ClinSpacer'
import { ClinText } from '../../../components/ClinText'
import {
  HoldDocumentDto,
  HoldDto,
  OrderLineDto,
  UploadedDocumentDto
} from '../../../types/swaggerTypes'
import Storage from '../../Products/ProductDetail/Storage/Storage'
import { StyledLinkButton } from '../../OrderPages/OrderDetail/OrderDetail.styles'
import { getOrderHoldsInformation } from '../../../services/Holds/holds'
import {
  uploadDocument,
  removeHoldDocument
} from '../../../services/ApiService'
import { OrderStatus } from '../../../types/OrderStatus'
import { ClinSpinner } from '../../../components/ClinSpinner'
import { ClinAnnounceBar } from '../../../components/ClinAnnounceBar'
import { DocumentUpload } from '../../OrderPages/OrderDetail/DocumentUpload/DocumentUpload'
import { AnnounceMode } from '../../../components/ClinAnnounceBar/ClinAnnounceBar'
import { createPortal } from 'react-dom'
import {
  StyledDocumentsAnnounceBar,
  StyledDocumentsAnnounceBarText,
  StyledSuccessNotification
} from '../../OrderPages/OrderDetail/ProductInformation/ProductInformation.styles'
import { ClinIconPathName } from '../../../components/ClinIcon/ClinIcon.paths'
import analyticsServiceSingleton from '../../../services/Analytics/initAnalytics'
import { AnalyticsEvent } from '../../../services/Analytics'

interface ProductInformationProps {
  isReassigned?: boolean
  isEnrolledProgram?: boolean
  orderId: string
  orderLine: OrderLineDto
  programSponsor: string | undefined
  handleFileUploadedForOrderAndHold?: (
    orderId: string,
    orderLine: OrderLineDto,
    hold: HoldDto,
    document: UploadedDocumentDto
  ) => void
  handleFileDeleteForOrderAndHold?: (
    orderLine: OrderLineDto,
    hold: HoldDto,
    documentId?: number
  ) => void
  orderStatus: string
  orderLines?: OrderLineDto[]
}

interface SuccessMessage {
  id: string
  text: string
  timestamp: number
}

const ProductInformation: React.FC<ProductInformationProps> = (props) => {
  const {
    isReassigned,
    isEnrolledProgram,
    orderLine,
    programSponsor,
    orderId,
    handleFileUploadedForOrderAndHold,
    handleFileDeleteForOrderAndHold,
    orderStatus,
    orderLines
  } = props
  const { t } = useTranslation()
  const holdsInformation = getOrderHoldsInformation(orderLine.holds)
  const [selectedHold, setSelectedHold] = useState<HoldDto | null>(null)
  const [successMessages, setSuccessMessages] = useState<SuccessMessage[]>([])
  const [notificationsContainer, setNotificationsContainer] =
    useState<HTMLElement | null>(null)
  const announceBarFileInputRef = useRef<HTMLInputElement | null>(null)
  const documentSectionFileInputRef = useRef<HTMLInputElement | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isDocumentLoading, setIsDocumentLoading] = useState<boolean>(false)
  const [removingDocumentId, setRemovingDocumentId] = useState<
    number | undefined
  >(undefined)
  const [announceBarContainer, setAnnounceBarContainer] =
    useState<HTMLElement | null>(null)
  const [shouldRerenderAnnounceBar, setShouldRerenderAnnounceBar] =
    useState<boolean>(false)
  const [shouldHideAnnounceBar, setShouldHideAnnounceBar] =
    useState<boolean>(false)

  const orderStatusIsNotShipped = orderStatus !== OrderStatus.Shipped

  const handleButtonClick = (
    e: React.MouseEvent,
    inputRef: React.RefObject<HTMLInputElement>
  ) => {
    e.stopPropagation()

    if (holdsInformation.holdsDetails.length > 1 && !selectedHold) {
      const firstHoldNeedingDocument = holdsInformation.holdsDetails.find(
        (hold) => hold.holdDto?.holdId && hold.requiresDocumentUpload
      )
      if (firstHoldNeedingDocument?.holdDto) {
        setSelectedHold(firstHoldNeedingDocument.holdDto)
      }
    }
    if (inputRef.current) {
      inputRef.current.click()
    }
  }

  const addSuccessMessage = (text: string) => {
    const newMessage = {
      id: `msg-${Date.now()}`,
      text: text,
      timestamp: Date.now()
    }
    setSuccessMessages((prev) => [...prev, newMessage])
  }

  const removeSuccessMessage = (id: string) => {
    setSuccessMessages((prev) => prev.filter((msg) => msg.id !== id))
  }

  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    inputRef: React.RefObject<HTMLInputElement>
  ) => {
    e.stopPropagation()
    const file = e.target.files && e.target.files[0]
    if (!file) return

    const allowedTypes = ['.docx', '.doc', '.pdf', '.jpg', '.jpeg']
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase()
    if (!allowedTypes.includes(fileExtension)) {
      setError(
        'There was an error uploading your document. Please try again and contact our Customer Services team if the problem persists'
      )
      if (inputRef.current) {
        inputRef.current.value = ''
      }
      return
    }

    setError(null)
    setIsDocumentLoading(true)
    uploadDocument(file)
      .then((response) => {
        const document = response.data

        const targetHold =
          selectedHold || (holdsInformation.holdsDetails[0]?.holdDto as HoldDto)

        if (handleFileUploadedForOrderAndHold && targetHold?.holdId) {
          handleFileUploadedForOrderAndHold(
            orderId,
            orderLine,
            targetHold,
            document
          )
        }
        addSuccessMessage('Document uploaded successfully')
      })
      .catch((err) => {
        setError(err.message)
        setIsDocumentLoading(false)
      })
      .finally(() => {
        setSelectedHold(null)
        setIsDocumentLoading(false)
        if (inputRef.current) {
          inputRef.current.value = ''
        }
      })
  }

  const handleFileDelete = (
    document: HoldDocumentDto,
    orderId: string,
    hold: HoldDto | undefined
  ) => {
    setError(null)
    setRemovingDocumentId(document?.documentId)

    removeHoldDocument(orderId, hold, document.documentId)
      .then(() => {
        if (handleFileDeleteForOrderAndHold && hold?.holdId) {
          handleFileDeleteForOrderAndHold(orderLine, hold, document?.documentId)
        }
        addSuccessMessage('Document deleted successfully')

        analyticsServiceSingleton.trackEvent(
          AnalyticsEvent.DeleteDocumentOrderDetails,
          {
            orderId: orderId,
            holdId: hold?.holdId,
            documentId: document.documentId,
            documentName: document.documentName,
            documentType: document.documentType
          }
        )
      })
      .catch((err) => {
        setError(err.message)
      })
      .finally(() => {
        setRemovingDocumentId(undefined)
      })
  }

  useEffect(() => {
    const containerId = 'notifications-container'
    let container = document.getElementById(containerId)
    if (!container) {
      container = document.createElement('div')
      container.id = containerId
      document.body.appendChild(container)
    }
    setNotificationsContainer(container)

    const announceBarContainerId =
      'order-detail-ma-documents-announce-bar-container'
    let announceContainer = document.getElementById(announceBarContainerId)
    if (!announceContainer) {
      announceContainer = document.createElement('div')
      announceContainer.id = announceBarContainerId
      document.body.appendChild(announceContainer)
    }
    setAnnounceBarContainer(announceContainer)

    return () => {
      if (
        container &&
        document.body.contains(container) &&
        container.childNodes.length === 0
      ) {
        try {
          document.body.removeChild(container)
        } catch (e) {
          console.error('Error removing notifications container:', e)
        }
      }

      if (
        announceContainer &&
        document.body.contains(announceContainer) &&
        announceContainer.childNodes.length === 0
      ) {
        try {
          document.body.removeChild(announceContainer)
        } catch (e) {
          console.error('Error removing announce bar container:', e)
        }
      }
    }
  }, [])
  useEffect(() => {
    if (!orderLines || !orderLines.length) return

    const countDocsForOrderLine = (ol: OrderLineDto): number => {
      return (ol.holds || []).reduce(
        (totalDocs, hold) => totalDocs + (hold.holdDocuments?.length || 0),
        0
      )
    }

    const currentOrderLineIndex = orderLines.findIndex((ol) => ol === orderLine)
    if (currentOrderLineIndex === -1) return

    const currentOrderLineDocCount = countDocsForOrderLine(orderLine)

    if (orderLines.length === 1) {
      setShouldHideAnnounceBar(currentOrderLineDocCount > 0)
      return
    }

    if (orderLines.length > 1) {
      const hasPreviousLineWithoutDocs = orderLines
        .slice(0, currentOrderLineIndex)
        .some((ol) => countDocsForOrderLine(ol) === 0)

      const hasCurrentLineAnyDocs = currentOrderLineDocCount > 0

      setShouldHideAnnounceBar(
        hasPreviousLineWithoutDocs || hasCurrentLineAnyDocs
      )

      if (
        currentOrderLineDocCount !==
        holdsInformation.holdsDetails.reduce(
          (totalDocs, hold) =>
            totalDocs + (hold.holdDto?.holdDocuments?.length || 0),
          0
        )
      ) {
        setShouldRerenderAnnounceBar(true)
        setTimeout(() => {
          setShouldRerenderAnnounceBar(false)
        }, 100)
      }
    }
  }, [orderLines, orderLine, holdsInformation.holdsDetails])
  return (
    <StyledProductInformation>
      <Row>
        <Column>
          <StyledItemDetails>
            <ClinText
              lineHeight={ClinTheme.lineHeights.body}
              fontSize={ClinTheme.fontSizes[1]}
              fontWeight={ClinTheme.fontWeights.bold}
            >
              {t('order_detail:product_information')}
            </ClinText>
          </StyledItemDetails>
          <StyledItemDetails>
            <ClinText
              lineHeight={ClinTheme.lineHeights.body}
              fontSize={ClinTheme.fontSizes[0]}
              fontWeight={ClinTheme.fontWeights.medium}
              color={ClinTheme.colors.darkGrey}
            >
              {t('glossary:sponsor')}
            </ClinText>
            <ClinText
              lineHeight={ClinTheme.lineHeights.body}
              fontSize={ClinTheme.fontSizes[1]}
              fontWeight={ClinTheme.fontWeights.medium}
            >
              {programSponsor || t('glossary:not_applicable')}
            </ClinText>
          </StyledItemDetails>
          <StyledItemDetails>
            <ClinText
              lineHeight={ClinTheme.lineHeights.body}
              fontSize={ClinTheme.fontSizes[0]}
              fontWeight={ClinTheme.fontWeights.medium}
              color={ClinTheme.colors.darkGrey}
            >
              {t('glossary:product_code')}
            </ClinText>
            <ClinText
              lineHeight={ClinTheme.lineHeights.body}
              fontSize={ClinTheme.fontSizes[1]}
              fontWeight={ClinTheme.fontWeights.medium}
            >
              {orderLine.skuCatalogItem?.sku ?? '--'}
            </ClinText>
          </StyledItemDetails>
        </Column>
        <Column>
          <StyledItemDetails>
            <ClinText
              lineHeight={ClinTheme.lineHeights.body}
              fontSize={ClinTheme.fontSizes[1]}
              fontWeight={ClinTheme.fontWeights.bold}
            >
              {t('glossary:licensing')}
            </ClinText>
          </StyledItemDetails>
          <StyledItemDetails>
            <ClinText
              lineHeight={ClinTheme.lineHeights.body}
              fontSize={ClinTheme.fontSizes[0]}
              fontWeight={ClinTheme.fontWeights.medium}
              color={ClinTheme.colors.darkGrey}
            >
              {t('glossary:country_of_license')}
            </ClinText>
            <ClinText
              lineHeight={ClinTheme.lineHeights.body}
              fontSize={ClinTheme.fontSizes[1]}
              fontWeight={ClinTheme.fontWeights.medium}
            >
              {orderLine.skuCatalogItem?.licenses?.length
                ? orderLine.skuCatalogItem?.licenses
                    .map((license: any) => license.country)
                    .join(', ')
                : '--'}
            </ClinText>
          </StyledItemDetails>
        </Column>
        <Column>
          <StyledItemDetails>
            <ClinText
              lineHeight={ClinTheme.lineHeights.body}
              fontSize={ClinTheme.fontSizes[1]}
              fontWeight={ClinTheme.fontWeights.bold}
            >
              {t('order_detail:handling_storage')}
            </ClinText>
          </StyledItemDetails>
          <StyledItemDetails>
            <ClinText
              lineHeight={ClinTheme.lineHeights.body}
              fontSize={ClinTheme.fontSizes[0]}
              fontWeight={ClinTheme.fontWeights.medium}
              color={ClinTheme.colors.darkGrey}
            >
              {t('glossary:cytotoxicity')}
            </ClinText>
            <ClinText
              lineHeight={ClinTheme.lineHeights.body}
              fontSize={ClinTheme.fontSizes[1]}
              fontWeight={ClinTheme.fontWeights.medium}
            >
              {orderLine.skuCatalogItem?.item.cytotoxic === 'Y' ? 'Yes' : 'No'}
            </ClinText>
          </StyledItemDetails>
          <StyledItemDetails>
            <ClinText
              lineHeight={ClinTheme.lineHeights.body}
              fontSize={ClinTheme.fontSizes[0]}
              fontWeight={ClinTheme.fontWeights.medium}
              color={ClinTheme.colors.darkGrey}
            >
              {t('glossary:storage')}
            </ClinText>
            {orderLine.skuCatalogItem?.item.storageTemperature ? (
              <StyledStorage>
                <Storage
                  storage={orderLine.skuCatalogItem?.item.storageTemperature}
                />
              </StyledStorage>
            ) : (
              <ClinText
                lineHeight={ClinTheme.lineHeights.body}
                fontSize={ClinTheme.fontSizes[1]}
                fontWeight={ClinTheme.fontWeights.medium}
              >
                --
              </ClinText>
            )}
          </StyledItemDetails>
        </Column>
      </Row>

      {holdsInformation.holdsDetails.length > 0 &&
        orderStatusIsNotShipped &&
        !isReassigned && (
          <div
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') e.stopPropagation()
            }}
            role="button"
            tabIndex={0}
          >
            {announceBarContainer &&
              !shouldHideAnnounceBar &&
              createPortal(
                <StyledDocumentsAnnounceBar
                  key={`announce-bar-${shouldRerenderAnnounceBar}`}
                  title=""
                  viewBox="0 0 22 22"
                  iconSize={24}
                  fillRule="evenodd"
                  clipRule="evenodd"
                  iconFill={ClinTheme.colors.orangeWarn}
                  iconName={ClinIconPathName.NewInformationAnnounce}
                  messageClassName="clin-announce-bar-message"
                  textClassName="clin-announce-bar-text"
                  mode={AnnounceMode.Information}
                >
                  <div className="clin-announce-bar-message-container">
                    <ClinText
                      lineHeight={ClinTheme.lineHeights.body}
                      fontSize={ClinTheme.fontSizes[1]}
                      fontWeight={ClinTheme.fontWeights.bold}
                    >
                      {t('order_detail:supporting_documents')}:
                    </ClinText>
                    <StyledDocumentsAnnounceBarText
                      lineHeight={ClinTheme.lineHeights.heading[0]}
                      fontSize={ClinTheme.fontSizes[1]}
                    >
                      <Trans
                        i18nKey="order_detail:document_notice"
                        components={{ strong: <strong /> }}
                      />
                    </StyledDocumentsAnnounceBarText>
                  </div>
                  <>
                    {isDocumentLoading ? (
                      <ClinSpinner size={20} />
                    ) : (
                      <>
                        <StyledLinkButton
                          variant="link"
                          disabled={!isEnrolledProgram}
                          onClick={(e) =>
                            handleButtonClick(e, announceBarFileInputRef)
                          }
                        >
                          {t('common:buttons.upload')}
                        </StyledLinkButton>
                        <input
                          ref={announceBarFileInputRef}
                          type="file"
                          style={{ display: 'none' }}
                          accept=".docx,.doc,.pdf,.jpg,.jpeg"
                          onChange={(e) =>
                            handleFileChange(e, announceBarFileInputRef)
                          }
                        />
                      </>
                    )}
                  </>
                </StyledDocumentsAnnounceBar>,
                announceBarContainer
              )}
            <ClinSpacer hasBorder color={ClinTheme.colors.grey} />
            <StyledDocumentsSection onClick={(e) => e.stopPropagation()}>
              <div>
                <ClinText
                  lineHeight={ClinTheme.lineHeights.body}
                  fontSize={ClinTheme.fontSizes[1]}
                  fontWeight={ClinTheme.fontWeights.bold}
                >
                  {t('order_detail:supporting_documents')}
                </ClinText>
                <ClinText
                  lineHeight={ClinTheme.lineHeights.heading[0]}
                  fontSize={ClinTheme.fontSizes[1]}
                >
                  <Trans
                    i18nKey="order_detail:documents_information"
                    components={{
                      bold: <strong />
                    }}
                  />
                </ClinText>
              </div>
              {isDocumentLoading ? (
                <ClinSpinner size={20} />
              ) : (
                <>
                  <StyledLinkButton
                    disabled={!isEnrolledProgram}
                    variant="link"
                    onClick={(e) =>
                      handleButtonClick(e, documentSectionFileInputRef)
                    }
                  >
                    {holdsInformation.holdsDetails.some(
                      (hold) =>
                        hold.holdDto?.holdDocuments?.length &&
                        hold.holdDto?.holdDocuments?.length > 0
                    )
                      ? 'Upload another document'
                      : t('order_detail:upload_document')}
                  </StyledLinkButton>
                  <input
                    ref={documentSectionFileInputRef}
                    type="file"
                    style={{ display: 'none' }}
                    accept=".docx,.doc,.pdf,.jpg,.jpeg"
                    onChange={(e) =>
                      handleFileChange(e, documentSectionFileInputRef)
                    }
                  />
                </>
              )}
            </StyledDocumentsSection>
            {error && (
              <ClinAnnounceBar
                isDismissible={true}
                mode={AnnounceMode.Error}
                title={' '}
                handleClose={(e) => {
                  e.stopPropagation()
                  setError(null)
                }}
              >
                <ClinText as={'div'} color={ClinTheme.colors.black}>
                  {error}
                </ClinText>
              </ClinAnnounceBar>
            )}
            {!error &&
              notificationsContainer &&
              createPortal(
                <StyledNotificationsContainer
                  onClick={(e) => {
                    e.stopPropagation()
                  }}
                >
                  {successMessages.map((message) => (
                    <StyledSuccessNotification
                      key={message.id}
                      autoHideTimer={10000}
                      title={message.text}
                      onClose={(e) => {
                        e?.stopPropagation()
                        removeSuccessMessage(message.id)
                      }}
                    />
                  ))}
                </StyledNotificationsContainer>,
                notificationsContainer
              )}
            {holdsInformation.holdsDetails.map((hold) =>
              hold.holdDto?.holdDocuments.map((document, index) => (
                <DocumentUpload
                  error={error}
                  key={`doc-upload-${hold.holdDto?.holdId}-${index}`}
                  prompt={document.documentType}
                  fileName={document.documentName}
                  document={document}
                  isPreOrderVariant={true}
                  showRemoveFile={true}
                  handleClickRemove={(e) => {
                    e.stopPropagation()
                    handleFileDelete(document, orderId, hold.holdDto)
                  }}
                  removingDocumentId={removingDocumentId}
                  uploadDetails={{
                    uploadDate: document.uploadedDate,
                    uploadedBy: document.uploadedBy
                  }}
                />
              ))
            )}
          </div>
        )}
    </StyledProductInformation>
  )
}

export default ProductInformation
